import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { MapPin, X, Search, Star, MessageCircle } from "lucide-react";
import { HotelNavPills } from "@/pages/HotelDashboard";
import { StarsInput, StarsReadOnly } from "@/components/Stars";
import ChatBox from "@/components/ChatBox";
import DriverApproachETA from "@/components/DriverApproachETA";
import BookingAcceptedCard from "@/components/BookingAcceptedCard";

const STATUS_LABEL = {
  pending: "En attente",
  accepted: "Acceptée",
  picked_up: "Client pris en charge",
  in_progress: "Trajet en cours",
  completed: "Terminée",
  cancelled: "Annulée",
};

export default function HotelHistory() {
  const { profile } = useAuth();
  const nav = useNavigate();
  const [bookings, setBookings] = useState([]);
  const [q, setQ] = useState("");
  const [filter, setFilter] = useState("all");
  const [receptionistFilter, setReceptionistFilter] = useState("all"); // "all" | "<id>" | "__none__"
  const [receptionists, setReceptionists] = useState([]);
  const [cancelId, setCancelId] = useState(null);
  const [ratingBooking, setRatingBooking] = useState(null);
  const [ratingStars, setRatingStars] = useState(0);
  const [ratingComment, setRatingComment] = useState("");
  const [ratingSaving, setRatingSaving] = useState(false);
  const [chatBooking, setChatBooking] = useState(null);

  const load = async () => {
    try {
      const { data } = await api.get("/hotel/bookings");
      setBookings(data.bookings || []);
    } catch {}
  };
  useEffect(() => { load(); }, []);

  // Fetch receptionists once (hotel-side, read-only).
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await api.get("/hotel/receptionists");
        if (!cancelled) setReceptionists(r.data.receptionists || []);
      } catch (_) {
        if (!cancelled) setReceptionists([]);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const confirmCancel = async () => {
    try {
      await api.post(`/hotel/bookings/${cancelId}/cancel`);
      toast.success("Course annulée");
      setCancelId(null);
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    }
  };

  const openRating = (booking) => {
    setRatingBooking(booking);
    setRatingStars(0);
    setRatingComment("");
  };

  const submitRating = async () => {
    if (!ratingStars) {
      toast.error("Sélectionnez une note");
      return;
    }
    setRatingSaving(true);
    try {
      await api.post(`/hotel/bookings/${ratingBooking.id}/rate`, {
        stars: ratingStars,
        comment: ratingComment,
      });
      toast.success("Merci pour votre évaluation !");
      setRatingBooking(null);
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    } finally {
      setRatingSaving(false);
    }
  };

  const filtered = bookings.filter((b) => {
    const qok = !q || (b.client_name + b.arrival_address + (b.room_number || "")).toLowerCase().includes(q.toLowerCase());
    if (receptionistFilter !== "all") {
      if (receptionistFilter === "__none__") {
        if (b.receptionist_id) return false;
      } else if (b.receptionist_id !== receptionistFilter) {
        return false;
      }
    }
    if (filter === "all") return qok;
    if (filter === "active") return qok && ["pending", "accepted", "picked_up", "in_progress"].includes(b.status);
    if (filter === "completed") return qok && b.status === "completed";
    if (filter === "cancelled") return qok && b.status === "cancelled";
    if (filter === "unpaid") return qok && b.payment_status !== "paid" && b.status !== "cancelled";
    return qok;
  });

  return (
    <div className="mx-auto max-w-screen-2xl px-4 pb-8 safe-top sm:px-6 sm:pb-10 lg:px-10" data-testid="hotel-history">
      <p className="text-xs uppercase tracking-widest text-primary">Tableau de bord</p>
      <h1 className="mt-2 font-[Outfit] text-3xl font-normal tracking-tight sm:text-4xl">{profile?.hotel_name || "Votre hôtel"}</h1>
      <p className="mt-1 text-sm text-muted-foreground">Historique et suivi de vos courses</p>

      <HotelNavPills active="history" />

      <div className="mt-6 flex flex-wrap items-center gap-2 sm:mt-8 sm:gap-3">
        <div className="relative w-full min-w-[260px] sm:flex-1 sm:max-w-md">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <input
            value={q} onChange={(e) => setQ(e.target.value)}
            placeholder="Rechercher un client, une adresse..."
            className="w-full rounded-lg border border-border bg-card py-2.5 pl-9 pr-3 text-sm outline-none focus:border-primary"
            data-testid="history-search"
          />
        </div>
        <div className="flex w-full flex-wrap gap-1.5 sm:w-auto sm:gap-2">
          {[["all","Toutes"],["active","En cours"],["unpaid","Non payées"],["completed","Terminées"],["cancelled","Annulées"]].map(([v, l]) => (
            <Button key={v} size="sm" variant={filter === v ? "default" : "outline"} onClick={() => setFilter(v)} data-testid={`filter-${v}`} className="text-xs sm:text-sm">
              {l}
            </Button>
          ))}
        </div>
      </div>

      {receptionists.length > 0 && (
        <div className="mt-3 flex flex-wrap items-center gap-1.5" data-testid="history-receptionist-filter">
          <span className="text-xs uppercase tracking-wide text-muted-foreground mr-1">Réceptionniste :</span>
          {[{ id: "all", name: "Tous" }, { id: "__none__", name: "Sans réceptionniste" }, ...receptionists].map((r) => {
            const active = receptionistFilter === r.id;
            return (
              <button
                key={r.id}
                type="button"
                onClick={() => setReceptionistFilter(r.id)}
                data-testid={`history-receptionist-pick-${r.id}`}
                className={`rounded-full border px-3 py-1 text-xs transition-colors ${
                  active
                    ? "border-primary bg-primary text-primary-foreground"
                    : "border-border bg-card text-foreground/70 hover:border-primary/40"
                }`}
              >
                {r.name}
              </button>
            );
          })}
        </div>
      )}

      <div className="mt-6 space-y-3" data-testid="bookings-list">
        {filtered.length === 0 ? (
          <div className="rounded-2xl border border-border bg-card p-10 text-center text-sm text-muted-foreground">
            Aucune réservation pour ces critères.
          </div>
        ) : filtered.map((b) => {
          const isAccepted = b.status === "accepted" && b.driver_id;
          const liveEtaActive = isAccepted;
          const showStaticEta = b.status === "picked_up" && b.eta_minutes && b.eta_set_at;
          const etaRemaining = showStaticEta ? Math.ceil(b.eta_minutes - (Date.now() - new Date(b.eta_set_at).getTime()) / 60000) : null;
          const canCancel = !["completed", "cancelled"].includes(b.status);
          return (
            <div key={b.id} className="flex flex-col gap-3 rounded-2xl border border-border bg-card p-5 sm:flex-row sm:items-center sm:justify-between" data-testid={`booking-${b.id}`}>
              <div className="min-w-0 flex-1">
                <div className="flex flex-wrap items-center gap-2">
                  <Badge variant={b.status === "completed" ? "default" : b.status === "cancelled" ? "destructive" : "secondary"} data-testid={`booking-status-${b.id}`}>
                    {STATUS_LABEL[b.status]}
                  </Badge>
                  <Badge variant={b.payment_status === "paid" ? "default" : "outline"} className={b.payment_status === "paid" ? "bg-[hsl(var(--success))] text-white" : ""}>
                    {b.payment_status === "paid" ? "Payée" : "Non payée"}
                  </Badge>
                  {etaRemaining != null && (
                    <Badge className="bg-primary text-primary-foreground" data-testid={`booking-eta-${b.id}`}>
                      {etaRemaining > 0 ? `Arrivée dans ~${etaRemaining} min` : "Chauffeur arrivé"}
                    </Badge>
                  )}
                  <span className="text-xs text-muted-foreground">{new Date(b.created_at).toLocaleString("fr-FR")}</span>
                </div>
                <p className="mt-2 font-medium">{b.client_name}</p>
                {b.receptionist_name && (
                  <p className="text-[11px] text-muted-foreground" data-testid={`booking-receptionist-${b.id}`}>
                    Réservée par <span className="font-medium text-foreground/80">{b.receptionist_name}</span>
                  </p>
                )}
                <p className="text-sm text-muted-foreground">→ {b.arrival_address}</p>
                <p className="text-xs text-muted-foreground">
                  {b.passenger_count} pers · {b.luggage_count} bagages · {b.price} €
                  {b.driver_name && !isAccepted && (
                    <> · Chauffeur : <strong className="text-foreground font-medium">{b.driver_name}</strong>
                      {b.driver_vehicle_brand_model && <> · 🚗 {b.driver_vehicle_brand_model}</>}
                      {b.driver_license_plate && (
                        <> · <span className="rounded border border-primary/30 bg-primary/5 px-1.5 py-0.5 font-mono font-semibold tracking-wider text-primary" data-testid={`driver-plate-${b.id}`}>{b.driver_license_plate}</span></>
                      )}
                    </>
                  )}
                  {b.payment_method && <> · Payé par : {b.payment_method}</>}
                </p>
                {isAccepted && (
                  <div className="mt-4">
                    <BookingAcceptedCard
                      booking={b}
                      onOpenChat={() => setChatBooking(b)}
                    />
                  </div>
                )}
              </div>
              <div className="flex flex-wrap items-center gap-2">
                {["accepted", "picked_up", "in_progress"].includes(b.status) && (
                  <Button variant="outline" size="sm" onClick={() => setChatBooking(b)} data-testid={`chat-${b.id}`}>
                    <MessageCircle className="mr-1.5 h-3.5 w-3.5" /> Chat chauffeur
                  </Button>
                )}
                {b.payment_status === "paid" && b.status !== "cancelled" && (
                  <span className="inline-flex items-center gap-1 rounded-md border border-[hsl(var(--success))]/40 bg-[hsl(var(--success))]/10 px-2 py-1 text-xs text-[hsl(var(--success))]" data-testid={`paid-badge-${b.id}`}>
                    ✓ Payé
                  </span>
                )}
                {b.status === "completed" && b.driver_id && !b.rated && (
                  <Button size="sm" variant="outline" className="border-[#C4A482] text-[#C4A482] hover:bg-[#C4A482]/10" onClick={() => openRating(b)} data-testid={`rate-${b.id}`}>
                    <Star className="mr-1.5 h-3.5 w-3.5" /> Noter le chauffeur
                  </Button>
                )}
                {b.rated && (
                  <div className="inline-flex items-center gap-1.5 rounded-md border border-border bg-secondary/40 px-2.5 py-1 text-xs" data-testid={`rated-${b.id}`}>
                    <StarsReadOnly value={b.stars} size={12} /> <span className="text-muted-foreground">Noté {b.stars}/5</span>
                  </div>
                )}
                {canCancel && (
                  <Button size="sm" variant="destructive" onClick={() => setCancelId(b.id)} data-testid={`cancel-${b.id}`}>
                    <X className="mr-1.5 h-3.5 w-3.5" /> Annuler
                  </Button>
                )}
              </div>
            </div>
          );
        })}
      </div>

      <Dialog open={!!cancelId} onOpenChange={(o) => !o && setCancelId(null)}>
        <DialogContent data-testid="cancel-dialog">
          <DialogHeader>
            <DialogTitle>Annuler cette course ?</DialogTitle>
            <DialogDescription>Le chauffeur (s'il est assigné) sera notifié. Cette action est irréversible.</DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCancelId(null)}>Retour</Button>
            <Button variant="destructive" onClick={confirmCancel} data-testid="cancel-confirm">Confirmer l'annulation</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!chatBooking} onOpenChange={(o) => !o && setChatBooking(null)}>
        <DialogContent data-testid="chat-dialog" className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Messagerie avec le chauffeur</DialogTitle>
            <DialogDescription>
              {chatBooking?.driver_name ? `${chatBooking.driver_name} — course de ${chatBooking.client_name}` : "Course en cours"}
            </DialogDescription>
          </DialogHeader>
          {chatBooking && (
            <ChatBox
              token={chatBooking.tracking_token}
              author="hotel"
              label={profile?.hotel_name || "Hôtel"}
            />
          )}
        </DialogContent>
      </Dialog>


      <Dialog open={!!ratingBooking} onOpenChange={(o) => !o && setRatingBooking(null)}>
        <DialogContent data-testid="rating-dialog">
          <DialogHeader>
            <DialogTitle>Noter {ratingBooking?.driver_name || "le chauffeur"}</DialogTitle>
            <DialogDescription>
              Votre retour aide Saor à garantir un service d'exception pour la course de {ratingBooking?.client_name}.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="flex flex-col items-center gap-2 rounded-xl border border-border bg-secondary/30 p-6">
              <p className="text-xs uppercase tracking-widest text-muted-foreground">Votre note</p>
              <StarsInput value={ratingStars} onChange={setRatingStars} size={36} />
              <p className="text-xs text-muted-foreground" data-testid="rating-stars-label">
                {ratingStars === 0 ? "Cliquez sur une étoile" : `${ratingStars} / 5`}
              </p>
            </div>
            <div className="space-y-2">
              <label className="text-sm">Commentaire (facultatif)</label>
              <Textarea
                value={ratingComment}
                onChange={(e) => setRatingComment(e.target.value)}
                rows={3}
                placeholder="Ponctualité, courtoisie, tenue du véhicule..."
                data-testid="rating-comment"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setRatingBooking(null)} disabled={ratingSaving}>Annuler</Button>
            <Button
              className="bg-primary hover:bg-primary/90"
              onClick={submitRating}
              disabled={ratingSaving || !ratingStars}
              data-testid="rating-submit"
            >
              {ratingSaving ? "Envoi..." : "Envoyer ma note"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
